import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import BreadcrumbBack from '../components/BreadcrumbBack';
import ConnectAccountModal from '../components/ConnectAccountModal';
import PublishAccountChip from '../components/PublishAccountChip';
import { track } from '../utils/analytics';
import { usePublishAccount, PublishAccountState } from '../hooks/usePublishAccount';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type PublishPlatform = 'youtube' | 'instagram' | 'tiktok' | 'facebook' | 'linkedin' | 'x';

interface PlatformDef {
  id: PublishPlatform;
  name: string;
  icon: string;
  contentType: string;
}

const PLATFORMS: PlatformDef[] = [
  { id: 'youtube', name: 'YouTube Shorts', icon: '▶️', contentType: 'shorts' },
  { id: 'instagram', name: 'Instagram Reels', icon: '📸', contentType: 'reels' },
  { id: 'tiktok', name: 'TikTok', icon: '🎵', contentType: 'video' },
  { id: 'facebook', name: 'Facebook Reels', icon: '📘', contentType: 'reels' },
  { id: 'linkedin', name: 'LinkedIn', icon: '💼', contentType: 'post' },
  { id: 'x', name: 'X (Twitter)', icon: '𝕏', contentType: 'post' },
];

const PLATFORM_IDS = new Set<PublishPlatform>(PLATFORMS.map(platform => platform.id));

interface ShortInfo {
  id: string;
  title: string;
  caption: string;
  tags: string[];
  score: number;
  startTime: number;
  endTime: number;
  duration: number;
  fileUrl: string;
  fileSize: number;
  platform: string;
}

interface VideoDetail {
  id: string;
  name: string | null;
  file_name: string;
  duration: number | null;
  content_type: string;
  created_at: string;
  shorts: ShortInfo[];
}

interface ProjectInfo {
  id: string;
  name: string;
  description?: string | null;
}

interface PublishRecord {
  id: string;
  platform: string;
  content_type: string;
  status: string;
  platform_url: string | null;
  error: string | null;
  published_at: string | null;
}

interface PublishRequest {
  id: string;
  short_id: string;
  caption: string;
  schedule_type: string;
  scheduled_time: string | null;
  overall_status: string;
  records: PublishRecord[];
  created_at: string;
}

interface DraftRecord {
  linked_account_id?: string | null;
  id: string;
  platform: string;
  content_type: string;
  status: string;
}

interface DraftDetail {
  id: string;
  video_id: string;
  short_id: string;
  caption: string | null;
  hashtags: string[];
  schedule_type: string;
  scheduled_time: string | null;
  timezone: string;
  overall_status: string;
  project_id: string | null;
  project_name: string | null;
  title: string | null;
  updated_at: string;
  records: DraftRecord[];
}

const STATUS_STYLE: Record<string, { icon: string; color: string; bg: string }> = {
  draft: { icon: '📝', color: '#9CA3AF', bg: '#374151' },
  queued: { icon: '⏳', color: '#F59E0B', bg: '#422006' },
  publishing: { icon: '🔄', color: '#3B82F6', bg: '#1E3A5F' },
  published: { icon: '✅', color: '#10B981', bg: '#064E3B' },
  failed: { icon: '❌', color: '#EF4444', bg: '#450A0A' },
  scheduled: { icon: '📅', color: '#8B5CF6', bg: '#2E1065' },
  cancelled: { icon: '🚫', color: '#6B7280', bg: '#374151' },
};

interface PublishDashboardProps {
  projectIdOverride?: string;
  videoIdOverride?: string;
  draftIdOverride?: string;
  breadcrumbMode?: 'publish' | 'compose';
  /** Rendered as a column inside another page — skip Sidebar / Header / breadcrumb. */
  embedded?: boolean;
  /** Drives the selected short from the parent (the compose batch rail). */
  selectedShortId?: string;
  /** Publishing account state owned by the parent (compose header chip). Without it, this page manages its own. */
  publishAccount?: PublishAccountState;
}

const PublishDashboard: React.FC<PublishDashboardProps> = ({
  projectIdOverride,
  videoIdOverride,
  draftIdOverride,
  breadcrumbMode = 'publish',
  embedded = false,
  selectedShortId,
  publishAccount,
}) => {
  // Toggle for showing all platforms (must be before any conditional return)
  const [showAllPlatforms, setShowAllPlatforms] = useState(false);

  const params = useParams<{ projectId: string; videoId: string }>();
  const [searchParams] = useSearchParams();
  const projectId = projectIdOverride || params.projectId;
  const videoId = videoIdOverride || params.videoId;
  const ownPublishAccount = usePublishAccount(publishAccount ? null : projectId);
  const pa = publishAccount ?? ownPublishAccount;
  const setPublishAccountId = pa.setSelectedId;
  const requestedShortId = searchParams.get('shortId');
  const isComposeBreadcrumb = breadcrumbMode === 'compose';
  const [project, setProject] = useState<ProjectInfo | null>(null);
  const [video, setVideo] = useState<VideoDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [draftShortId, setDraftShortId] = useState<string | null>(null);

  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<PublishPlatform>>(new Set());
  const [caption, setCaption] = useState('');
  const [youtubeTitle, setYoutubeTitle] = useState('');
  const [youtubeTags, setYoutubeTags] = useState<string[]>([]);
  const [youtubeTagDraft, setYoutubeTagDraft] = useState('');
  const [youtubeAudience, setYoutubeAudience] = useState<'made_for_kids' | 'not_made_for_kids'>('not_made_for_kids');
  const [youtubePaidPromotion, setYoutubePaidPromotion] = useState(false);
  const [youtubeThumbnailDataUrl, setYoutubeThumbnailDataUrl] = useState<string | null>(null);
  const [youtubeThumbnailName, setYoutubeThumbnailName] = useState('');
  const [selectedShortIdx, setSelectedShortIdx] = useState(0);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('15:00');
  const [publishing, setPublishing] = useState(false);
  const [publishMode, setPublishMode] = useState<'publish' | 'schedule' | 'draft' | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [previewTab, setPreviewTab] = useState<'all' | PublishPlatform>('all');
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const thumbnailInputRef = useRef<HTMLInputElement | null>(null);

  // Publish history
  const [publishHistory, setPublishHistory] = useState<PublishRequest[]>([]);
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Toggle play/pause for all preview videos
  const togglePlayPause = useCallback(() => {
    const newPlayingState = !isPlaying;
    setIsPlaying(newPlayingState);
    videoRefs.current.forEach(video => {
      if (video) {
        if (newPlayingState) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    });
  }, [isPlaying]);

  // Connected platforms (from backend OAuth)
  const [connectedPlatforms, setConnectedPlatforms] = useState<Set<PublishPlatform>>(new Set());
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // Connect account modal
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [selectedPlatformToConnect, setSelectedPlatformToConnect] = useState<PlatformDef | null>(null);

  // Map OAuth provider to platform type
  const PROVIDER_TO_PLATFORM: Record<string, PublishPlatform> = {
    google: 'youtube',
    instagram: 'instagram',
    facebook: 'facebook',
    tiktok: 'tiktok',
    twitter: 'x',
    linkedin: 'linkedin',
  };

  const applyDraftToComposer = useCallback((draft: DraftDetail) => {
    const normalizedCaption = draft.caption || '';
    const normalizedTags = (draft.hashtags || []).map(tag => String(tag).trim()).filter(Boolean);
    const draftPlatforms = draft.records
      .map(record => record.platform)
      .filter((platform): platform is PublishPlatform => PLATFORM_IDS.has(platform as PublishPlatform));

    const youtubeRecord = draft.records.find(record => record.platform === 'youtube');
    if (youtubeRecord?.linked_account_id) setPublishAccountId(youtubeRecord.linked_account_id);

    setCaption(normalizedCaption);
    setYoutubeTags(normalizedTags);
    setSelectedPlatforms(new Set<PublishPlatform>(draftPlatforms));
    setDraftShortId(draft.short_id || null);

    if (draft.title && draft.title.trim()) {
      setYoutubeTitle(draft.title.trim());
    }

    if (draft.scheduled_time) {
      const parsed = new Date(draft.scheduled_time);
      if (!Number.isNaN(parsed.getTime())) {
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        const hours = String(parsed.getHours()).padStart(2, '0');
        const minutes = String(parsed.getMinutes()).padStart(2, '0');

        setScheduleEnabled(true);
        setScheduleDate(`${year}-${month}-${day}`);
        setScheduleTime(`${hours}:${minutes}`);
      }
    } else {
      setScheduleEnabled(false);
      setScheduleDate('');
      setScheduleTime('15:00');
    }
  }, [setPublishAccountId]);

  // Load connected accounts
  useEffect(() => {
    fetch(`${API_BASE}/api/accounts`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { accounts: [], connectedPlatforms: [] })
      .then(data => {
        const platforms = (data.connectedPlatforms || [])
          .map((value: string) => PROVIDER_TO_PLATFORM[value] || value)
          .filter(Boolean);

        const connected = new Set<PublishPlatform>(platforms as PublishPlatform[]);
        setConnectedPlatforms(connected);
        setLoadingAccounts(false);

        // If no platforms have been selected yet, default to all connected platforms
        setSelectedPlatforms(prev => {
          if (prev.size === 0) {
            return connected;
          }
          const next = new Set(prev);
          Array.from(prev).forEach(p => {
            if (!platforms.includes(p)) {
              next.delete(p);
            }
          });
          return next;
        });
      })
      .catch(() => setLoadingAccounts(false));
  }, []);

  // Load project + video data
  useEffect(() => {
    if (!projectId || !videoId) return;
    Promise.all([
      fetch(`${API_BASE}/api/projects/${projectId}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      fetch(`${API_BASE}/api/projects/${projectId}/videos/${videoId}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      fetch(`${API_BASE}/api/publish/video/${videoId}`, { credentials: 'include' }).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([p, v, history]) => {
      setProject(p);
      setVideo(v);
      if (Array.isArray(history)) setPublishHistory(history);
      if (!draftIdOverride) {
        const shorts = Array.isArray(v?.shorts) ? (v.shorts as ShortInfo[]) : [];
        const requestedIdx = requestedShortId
          ? shorts.findIndex((short: ShortInfo) => short.id === requestedShortId)
          : -1;
        const nextShortIdx = requestedIdx >= 0 ? requestedIdx : 0;

        if (shorts.length > 0) {
          setSelectedShortIdx(nextShortIdx);
        }

        const selectedShort = shorts[nextShortIdx];
        setYoutubeTitle((selectedShort?.title || p?.name || v?.name || v?.file_name || '').trim());
        setCaption((selectedShort?.caption || p?.description || '').trim());
        setYoutubeTags(((selectedShort?.tags || []) as string[]).map((tag: string) => String(tag).trim()).filter(Boolean));
        setYoutubeThumbnailDataUrl(null);
        setYoutubeThumbnailName('');
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [projectId, videoId, draftIdOverride, requestedShortId]);

  // Hydrate compose form when editing an existing draft
  useEffect(() => {
    if (!draftIdOverride) {
      setDraftShortId(null);
      return;
    }

    let cancelled = false;
    setLoadingDraft(true);

    fetch(`${API_BASE}/api/posts/drafts/${encodeURIComponent(draftIdOverride)}`, {
      credentials: 'include',
    })
      .then(async response => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({} as { error?: string }));
          throw new Error(payload.error || 'Failed to load draft');
        }

        return response.json() as Promise<DraftDetail>;
      })
      .then(draft => {
        if (cancelled) return;
        applyDraftToComposer(draft);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load draft';
        setPublishError(message);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingDraft(false);
      });

    return () => {
      cancelled = true;
    };
  }, [draftIdOverride, applyDraftToComposer]);

  useEffect(() => {
    if (!draftShortId || !video?.shorts?.length) {
      return;
    }

    const idx = video.shorts.findIndex(short => short.id === draftShortId);
    if (idx >= 0) {
      setSelectedShortIdx(idx);
    }
  }, [draftShortId, video]);

  // Parent-driven selection (compose batch rail).
  useEffect(() => {
    if (!selectedShortId || !video?.shorts?.length) return;
    const idx = video.shorts.findIndex(short => short.id === selectedShortId);
    if (idx >= 0) setSelectedShortIdx(idx);
  }, [selectedShortId, video]);

  useEffect(() => {
    if (draftIdOverride || !video?.shorts?.length) {
      return;
    }

    const selectedShort = video.shorts[selectedShortIdx];
    if (!selectedShort) {
      return;
    }

    setYoutubeTitle((selectedShort.title || video.name || video.file_name || '').trim());
    setCaption((selectedShort.caption || '').trim());
    setYoutubeTags(((selectedShort.tags || []) as string[]).map((tag: string) => String(tag).trim()).filter(Boolean));
    setYoutubeThumbnailDataUrl(null);
    setYoutubeThumbnailName('');
  }, [draftIdOverride, selectedShortIdx, video]);

  // Poll active publish request
  useEffect(() => {
    if (!activeRequestId) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/publish/${activeRequestId}`, { credentials: 'include' });
        if (!res.ok) return;
        const updated: PublishRequest = await res.json();

        setPublishHistory(prev => prev.map(r => r.id === updated.id ? updated : r));

        if (['published', 'failed', 'cancelled'].includes(updated.overall_status)) {
          setActiveRequestId(null);
          setPublishing(false);
          setPublishMode(null);
        }
      } catch { /* ignore */ }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeRequestId]);

  const togglePlatform = (p: PublishPlatform) => {
    setSelectedPlatforms(prev => {
      const next = new Set(prev);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });
  };

  const handlePlatformClick = (p: PlatformDef, isConnected: boolean) => {
    if (!isConnected) {
      // Open connect modal for disconnected platforms
      setSelectedPlatformToConnect(p);
      setConnectModalOpen(true);
    } else {
      // Toggle selection for connected platforms
      togglePlatform(p.id);
    }
  };

  const selectedShort = video?.shorts?.[selectedShortIdx] || null;

  // Required to Publish or Schedule; a draft can always be saved incomplete.
  // Mirrors the backend gate in POST /api/publish.
  // Gates every action button (including Save as Draft) — nothing to publish/save without these.
  const structuralBlockReason = useMemo<string | null>(() => {
    if (selectedPlatforms.size === 0) {
      return 'Select at least one platform to continue.';
    }
    if (!selectedShort) {
      return 'Select a short to continue.';
    }
    return null;
  }, [selectedPlatforms, selectedShort]);

  const publishBlockReason = useMemo<string | null>(() => {
    if (!caption.trim()) {
      return 'Add a caption to publish or schedule. You can still save as a draft.';
    }
    if (selectedPlatforms.has('youtube') && !youtubeTitle.trim()) {
      return 'Add a YouTube title to publish to YouTube.';
    }
    if (selectedPlatforms.has('youtube') && pa.needsChoice) {
      return 'Choose the YouTube account to publish to. You can still save as a draft.';
    }
    return null;
  }, [caption, selectedPlatforms, youtubeTitle, pa.needsChoice]);

  // Publish/Schedule need both gates; Save as Draft and the Schedule toggle only need the structural one.
  const submitBlockReason = structuralBlockReason || publishBlockReason;

  // "Publish to <account>" — the button names the account so a wrong one is hard to miss.
  const publishAccountName = selectedPlatforms.has('youtube') && pa.selected
    ? (pa.selected.name.length > 24 ? `${pa.selected.name.slice(0, 23)}…` : pa.selected.name)
    : null;
  const publishToLabel = publishAccountName
    ? `Publish to ${publishAccountName}${selectedPlatforms.size > 1 ? ` +${selectedPlatforms.size - 1}` : ''}`
    : null;

  const formatDuration = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days > 0) return `${days}d ago`;
    const hours = Math.floor(diff / 3600000);
    if (hours > 0) return `${hours}h ago`;
    return 'just now';
  };

  const youtubeTagsChars = youtubeTags.join(',').length;

  const parseYoutubeTags = useCallback((rawValue: string): string[] => {
    return rawValue
      .split(/,|#/g)
      .map(tag => tag.trim())
      .filter(Boolean);
  }, []);

  const addYoutubeTags = useCallback((rawValue: string) => {
    const parsedTags = parseYoutubeTags(rawValue);
    if (parsedTags.length === 0) return;

    setYoutubeTags(prev => {
      const next = [...prev];
      const existing = new Set(prev.map(t => t.toLowerCase()));
      let limitExceeded = false;
      let changed = false;

      for (const tag of parsedTags) {
        const normalized = tag.replace(/^#+/, '').trim();
        if (!normalized) continue;
        if (existing.has(normalized.toLowerCase())) continue;

        const candidate = [...next, normalized];
        if (candidate.join(',').length > 500) {
          limitExceeded = true;
          break;
        }

        next.push(normalized);
        existing.add(normalized.toLowerCase());
        changed = true;
      }

      if (limitExceeded) {
        setPublishError('YouTube tags cannot exceed 500 characters in total.');
        return prev;
      }

      return changed ? next : prev;
    });

    setPublishError(null);
  }, [parseYoutubeTags]);

  const removeYoutubeTag = useCallback((tagToRemove: string) => {
    setYoutubeTags(prev => prev.filter(tag => tag !== tagToRemove));
  }, []);

  const handleYoutubeTagsKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const value = youtubeTagDraft.trim();

    if (event.key === ',' || event.key === 'Enter') {
      event.preventDefault();
      addYoutubeTags(value);
      setYoutubeTagDraft('');
      return;
    }

    if (event.key === 'Backspace' && youtubeTagDraft.length === 0 && youtubeTags.length > 0) {
      const last = youtubeTags[youtubeTags.length - 1];
      removeYoutubeTag(last);
    }
  };

  const handleYoutubeTagsBlur = () => {
    const value = youtubeTagDraft.trim();
    if (!value) return;
    addYoutubeTags(value);
    setYoutubeTagDraft('');
  };

  const handleYoutubeTagsPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('text');
    if (!pastedText) return;

    const parsedTags = parseYoutubeTags(pastedText);
    if (parsedTags.length === 0) return;

    event.preventDefault();
    addYoutubeTags(pastedText);
    setYoutubeTagDraft('');
  };

  const handleYoutubeThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setYoutubeThumbnailDataUrl(null);
      setYoutubeThumbnailName('');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setPublishError('Thumbnail must be an image file.');
      event.currentTarget.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : null;
      if (!dataUrl) {
        setPublishError('Failed to read thumbnail file.');
        return;
      }
      setPublishError(null);
      setYoutubeThumbnailDataUrl(dataUrl);
      setYoutubeThumbnailName(file.name);
    };
    reader.onerror = () => setPublishError('Failed to read thumbnail file.');
    reader.readAsDataURL(file);
  };

  const handlePublish = useCallback(async (isScheduled: boolean, asDraft = false) => {
    if (publishing) return;
    if (structuralBlockReason) {
      setPublishError(structuralBlockReason);
      return;
    }
    if (!videoId || !selectedShort) return;

    // Required-field gate — publish / schedule only. Drafts skip it.
    if (!asDraft && publishBlockReason) {
      setPublishError(publishBlockReason);
      return;
    }

    // Validate all selected platforms are connected
    if (!asDraft) {
      const unconnectedPlatforms = Array.from(selectedPlatforms).filter(p => !connectedPlatforms.has(p));
      if (unconnectedPlatforms.length > 0) {
        const platformNames = unconnectedPlatforms
          .map(p => PLATFORMS.find(pf => pf.id === p)?.name || p)
          .join(', ');
        setPublishError(`Please connect the following platforms first: ${platformNames}`);
        return;
      }
    }

    const mode: 'publish' | 'schedule' | 'draft' = asDraft ? 'draft' : (isScheduled ? 'schedule' : 'publish');
    setPublishMode(mode);
    setPublishing(true);
    setPublishError(null);

    const platforms = Array.from(selectedPlatforms).map(pid => {
      const def = PLATFORMS.find(p => p.id === pid)!;
      return {
        platform: pid,
        contentType: def.contentType,
        ...(pid === 'youtube' && pa.selectedId ? { linkedAccountId: pa.selectedId } : {}),
      };
    });

    const hashtagsFromCaption = caption.match(/#\w+/g) || [];
    const hashtagsFromTags = youtubeTags.map(tag => tag.replace(/^#+/, '').trim()).filter(Boolean);

    const computedScheduleTime = (() => {
      if (!scheduleDate || !scheduleTime) {
        return null;
      }

      const parsed = new Date(`${scheduleDate}T${scheduleTime}`);
      if (Number.isNaN(parsed.getTime())) {
        return null;
      }

      return parsed.toISOString();
    })();

    let scheduledTime: string | null = null;
    if (!asDraft && isScheduled) {
      scheduledTime = computedScheduleTime;
    }

    try {
      if (asDraft && draftIdOverride) {
        const updateRes = await fetch(`${API_BASE}/api/posts/drafts/${encodeURIComponent(draftIdOverride)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            videoId,
            shortId: selectedShort.id,
            caption,
            hashtags: hashtagsFromTags,
            scheduledTime: scheduleEnabled ? computedScheduleTime : null,
            timezone: 'PST',
            platforms,
          }),
        });

        if (!updateRes.ok) {
          const data = await updateRes.json().catch(() => ({ error: 'Failed to save draft' }));
          throw new Error(data.error || 'Failed to save draft');
        }

        const updatedDraft = await updateRes.json() as DraftDetail;
        applyDraftToComposer(updatedDraft);
        setPublishing(false);
        setPublishMode(null);
        return;
      }

      const res = await fetch(`${API_BASE}/api/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          videoId,
          shortId: selectedShort.id,
          caption,
          hashtags: hashtagsFromCaption,
          scheduleType: asDraft ? 'draft' : (isScheduled ? 'scheduled' : 'immediate'),
          scheduledTime,
          timezone: 'PST',
          platforms,
          asDraft,
          youtubeOptions: selectedPlatforms.has('youtube') ? {
            title: youtubeTitle.trim() || selectedShort.title || '',
            description: caption,
            tags: youtubeTags,
            audience: youtubeAudience,
            paidPromotion: youtubePaidPromotion,
            thumbnailDataUrl: youtubeThumbnailDataUrl,
            thumbnailFileName: youtubeThumbnailName || null,
          } : undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Publish failed' }));
        throw new Error(data.error);
      }

      const publishReq: PublishRequest = await res.json();
      track('publish_submitted', {
        platforms: platforms.map(p => p.platform).join(','),
        schedule_type: asDraft ? 'draft' : (isScheduled ? 'scheduled' : 'immediate'),
      });
      setPublishHistory(prev => [publishReq, ...prev]);

      if (!isScheduled && !asDraft) {
        setActiveRequestId(publishReq.id);
      } else {
        setPublishing(false);
        setPublishMode(null);
      }
    } catch (err: any) {
      setPublishError(err.message);
      setPublishing(false);
      setPublishMode(null);
    }
  }, [publishing, structuralBlockReason, selectedPlatforms, selectedShort, videoId, caption, scheduleDate, scheduleTime, connectedPlatforms, youtubeTags, youtubeTitle, youtubeAudience, youtubePaidPromotion, youtubeThumbnailDataUrl, youtubeThumbnailName, draftIdOverride, scheduleEnabled, applyDraftToComposer, publishBlockReason, pa.selectedId]);

  if (loading) {
    if (embedded) return <div className="project-loading">Loading...</div>;
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content">
          <Header />
          <div className="project-loading">Loading...</div>
        </main>
      </div>
    );
  }

  if (!video) {
    if (embedded) return <div className="project-empty"><h3>Video not found</h3></div>;
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content">
          <Header />
          <div className="project-empty">
            <h3>Video not found</h3>
            <Link to={isComposeBreadcrumb ? '/posts/compose' : '/projects'} className="btn-primary">
              {isComposeBreadcrumb ? '← Back to Compose' : '← Back to Projects'}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const videoName = video.name || video.file_name;
  const activePlatforms = PLATFORMS.filter(p => selectedPlatforms.has(p.id));



  const body = (
    <>
        {/* Breadcrumb */}
        {!embedded && (
        <div className="breadcrumb">
          <div className="breadcrumb-links">
            {isComposeBreadcrumb ? (
              <>
                <BreadcrumbBack to="/posts/compose" />
                <Link to="/posts">Posts</Link>
                <span className="breadcrumb-sep">›</span>
                <Link to="/posts/compose">Compose</Link>
                <span className="breadcrumb-sep">›</span>
                <span>{videoName}</span>
              </>
            ) : (
              <>
                <BreadcrumbBack to={`/projects/${projectId}/video/${videoId}`} />
                <Link to="/projects">Projects</Link>
                <span className="breadcrumb-sep">›</span>
                <Link to={`/projects/${projectId}`}>{project?.name || 'Project'}</Link>
                <span className="breadcrumb-sep">›</span>
                <Link to={`/projects/${projectId}/video/${videoId}`}>{videoName}</Link>
                <span className="breadcrumb-sep">›</span>
                <span>Publish</span>
              </>
            )}
          </div>
        </div>
        )}

        {loadingDraft && (
          <div style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: 13 }}>
            Loading draft details...
          </div>
        )}

        {publishError && <div className="error-banner">⚠️ {publishError}</div>}
        {!publishError && publishBlockReason && (
          <div className="pub-validation-hint">ⓘ {publishBlockReason}</div>
        )}

        {/* Two-panel layout */}
        <div className="pub-layout">
          {/* Left Panel — Form */}
          <div className="pub-left">

            {/* Platforms */}
            <div className="pub-section">
              <div className="pub-section-header">
                <div className="pub-section-header-title">
                  Social Accounts
                </div>
                <div className="pub-section-header-actions">
                  <div
                    className="pub-platform-toggle"
                    onClick={() => setShowAllPlatforms(v => !v)}
                  >
                    <span className="pub-platform-toggle-label">
                      {showAllPlatforms ? 'All' : 'Connected'}
                    </span>
                    <div className={`pub-platform-toggle-track ${showAllPlatforms ? 'active' : ''}`}>
                      <div className="pub-platform-toggle-thumb" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="pub-platform-grid">
                {(showAllPlatforms ? PLATFORMS : PLATFORMS.filter(p => connectedPlatforms.has(p.id))).map(p => {
                  const isConnected = connectedPlatforms.has(p.id);
                  return (
                    <div
                      key={p.id}
                      className={`pub-platform-card ${selectedPlatforms.has(p.id) ? 'active' : ''} ${!isConnected ? 'disconnected' : ''}`}
                      onClick={() => handlePlatformClick(p, isConnected)}
                      title={!isConnected ? `Click to connect ${p.name}` : ''}
                    >
                      <span className="pub-platform-icon">{p.icon}</span>
                      <span className="pub-platform-name">{p.name}</span>
                      {!isConnected && <span className="pub-platform-status">⚠️ Not connected</span>}
                      <span className="pub-platform-check">
                        {!isConnected ? '🔒' : (selectedPlatforms.has(p.id) ? '☑' : '☐')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* YouTube Metadata */}
            <div className="pub-section">
              <div className="pub-section-header">
                <h3>Post Details</h3>
              </div>
              <div className="pub-yt-component pub-field">
                <label>Title</label>
                <input
                  type="text"
                  className="pub-input se-input"
                  value={youtubeTitle}
                  onChange={e => setYoutubeTitle(e.target.value)}
                  placeholder="Enter video title..."
                />
              </div>

              <div className="pub-yt-component pub-field">
                <label>Description</label>
                <textarea
                  className="pub-caption se-textarea"
                  value={caption}
                  onChange={e => setCaption(e.target.value)}
                  rows={4}
                  placeholder="Write your description..."
                />
                <div className="pub-caption-count">{caption.length} chars</div>
              </div>

              <div className="pub-yt-component pub-field">
                <div className="pub-schedule-row">
                  <div className="pub-field">
                  <label>Audience</label>
                  <select
                    className="pub-input se-select"
                    value={youtubeAudience}
                    onChange={e => setYoutubeAudience(e.target.value as 'made_for_kids' | 'not_made_for_kids')}
                  >
                    <option value="not_made_for_kids">Not made for kids</option>
                    <option value="made_for_kids">Made for kids</option>
                  </select>
                </div>
                </div>
              </div>

              <div className="pub-yt-component pub-field">
                <label>Tags</label>
                <div className="pub-tags-note">
                  Tags can be useful if content in your video is commonly misspelled. Otherwise, tags play a minimal role in helping viewers find your video.
                </div>
                <div className="pub-tags-editor">
                  {youtubeTags.map((tag) => (
                    <span key={tag} className="pub-tag-chip">
                      {tag}
                      <button type="button" aria-label={`Remove ${tag}`} onClick={() => removeYoutubeTag(tag)}>×</button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="pub-tag-input se-input"
                    value={youtubeTagDraft}
                    onChange={e => setYoutubeTagDraft(e.target.value)}
                    onKeyDown={handleYoutubeTagsKeyDown}
                    onBlur={handleYoutubeTagsBlur}
                    onPaste={handleYoutubeTagsPaste}
                    placeholder={youtubeTags.length === 0 ? 'Separate tags with comma or #hashtag' : ''}
                  />
                </div>
                <div className="pub-tags-footer">
                  <span>Use comma or # to separate tags</span>
                  <span>{youtubeTagsChars}/500</span>
                </div>
              </div>

              <div className="pub-yt-component pub-field">
                <label className="pub-check-row">
                  <input
                    type="checkbox"
                    checked={youtubePaidPromotion}
                    onChange={e => setYoutubePaidPromotion(e.target.checked)}
                  />
                  <span>Paid Promotion</span>
                </label>
              </div>

              <div className="pub-help-text">These settings are applied when publishing.</div>
            </div>

            {/* Video File Attachment */}
            <div className="pub-section">
              <h3>Video File Attachment</h3>
              {!embedded && video.shorts.length > 1 && (
                <div className="pub-short-selector">
                  {video.shorts.map((s, i) => (
                    <button
                      key={s.id}
                      className={`pub-short-pill ${i === selectedShortIdx ? 'active' : ''}`}
                      onClick={() => setSelectedShortIdx(i)}
                    >
                      Short #{i + 1}
                    </button>
                  ))}
                </div>
              )}
              <div className="pub-media-layout">
                <div className="pub-media-main">
                  {selectedShort && (
                    <div className="pub-attachment">
                      <div className="pub-attachment-thumb">▶</div>
                      <div className="pub-attachment-info">
                        <span className="pub-attachment-name">
                          {selectedShort.title} ({formatDuration(selectedShort.duration)})
                        </span>
                        <span className="pub-attachment-meta">{timeAgo(video.created_at)}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pub-media-thumb pub-yt-component pub-field">
                  <label>Thumbnail</label>
                  <div className="pub-thumbnail-preview-wrap">
                    {youtubeThumbnailDataUrl ? (
                      <img className="pub-thumbnail-preview" src={youtubeThumbnailDataUrl} alt="Thumbnail preview" />
                    ) : (
                      <div className="pub-thumbnail-placeholder">No thumbnail selected</div>
                    )}
                  </div>
                  <div className="pub-thumbnail-row">
                    <input
                      ref={thumbnailInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleYoutubeThumbnailChange}
                      className="pub-thumbnail-input-hidden"
                    />
                    <button
                      type="button"
                      className="se-btn-upload pub-thumbnail-upload-btn"
                      onClick={() => thumbnailInputRef.current?.click()}
                    >
                      Upload Files
                    </button>
                    {youtubeThumbnailDataUrl && (
                      <button
                        type="button"
                        className="se-btn-upload"
                        onClick={() => {
                          setYoutubeThumbnailDataUrl(null);
                          setYoutubeThumbnailName('');
                          if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {youtubeThumbnailName && <div className="pub-help-text">{youtubeThumbnailName}</div>}
                </div>
              </div>
            </div>

            {/* Date & Time */}
            <div className="pub-section">
              <div className="pub-section-header">
                <h3>Schedule post</h3>
                <label className="pub-toggle">
                  <input
                    type="checkbox"
                    checked={scheduleEnabled}
                    onChange={e => setScheduleEnabled(e.target.checked)}
                  />
                  <span className="pub-toggle-slider" />
                </label>
              </div>
              {scheduleEnabled && (
                <div className="pub-schedule-row">
                  <div className="pub-field">
                    <label>Date</label>
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={e => setScheduleDate(e.target.value)}
                    />
                  </div>
                  <div className="pub-field">
                    <label>Time</label>
                    <div className="pub-time-row">
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={e => setScheduleTime(e.target.value)}
                      />
                      <span className="pub-tz">PST</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Publish History */}
            {publishHistory.length > 0 && (
              <div className="pub-section">
                <h3>Publish History</h3>
                <div className="pub-history-list">
                  {publishHistory.map(req => {
                    const shortInfo = video.shorts.find(s => s.id === req.short_id);
                    const st = STATUS_STYLE[req.overall_status] || STATUS_STYLE.draft;
                    return (
                      <div key={req.id} className="pub-history-item">
                        <div className="pub-history-header">
                          <span className="pub-history-title">{shortInfo?.title || 'Short'}</span>
                          <span className="pub-history-badge" style={{ color: st.color, background: st.bg }}>
                            {st.icon} {req.overall_status}
                          </span>
                        </div>
                        <div className="pub-history-records">
                          {req.records.map(rec => {
                            const pDef = PLATFORMS.find(p => p.id === rec.platform);
                            const rs = STATUS_STYLE[rec.status] || STATUS_STYLE.draft;
                            return (
                              <div key={rec.id} className="pub-history-record">
                                <span>{pDef?.icon || '🌐'} {pDef?.name || rec.platform}</span>
                                <span style={{ color: rs.color }}>{rs.icon} {rec.status}</span>
                                {rec.platform_url && (
                                  <a href={rec.platform_url} target="_blank" rel="noopener noreferrer" className="pub-history-link">
                                    🔗
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <span className="pub-history-time">{timeAgo(req.created_at)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right Panel — Previews */}
          <div className="pub-right">
            <div className="pub-preview-tabs">
              <button
                className="pub-preview-control"
                onClick={togglePlayPause}
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
              <button
                className={`pub-preview-tab ${previewTab === 'all' ? 'active' : ''}`}
                onClick={() => setPreviewTab('all')}
              >
                All Platforms
              </button>
              {activePlatforms.map(p => (
                <button
                  key={p.id}
                  className={`pub-preview-tab ${previewTab === p.id ? 'active' : ''}`}
                  onClick={() => setPreviewTab(p.id)}
                >
                  {p.name.split(' ')[0]}
                </button>
              ))}
            </div>

            <div className={`pub-preview-grid ${previewTab !== 'all' ? 'single' : ''}`}>
              {(previewTab === 'all' ? activePlatforms : activePlatforms.filter(p => p.id === previewTab)).map((p, idx) => (
                <div key={p.id} className="pub-phone-frame">
                  <div className="pub-phone-status-bar">
                    <span>9:41</span>
                    <span>●●● ▸▸</span>
                  </div>
                  <div className="pub-phone-chrome">
                    <span className="pub-phone-platform-icon">{p.icon}</span>
                    <span className="pub-phone-platform-label">{p.name}</span>
                  </div>
                  <div className="pub-phone-video">
                    {selectedShort?.fileUrl ? (
                      <video
                        ref={el => { videoRefs.current[idx] = el; }}
                        src={selectedShort.fileUrl.startsWith('http') ? selectedShort.fileUrl : `${API_BASE}${selectedShort.fileUrl}`}
                        muted
                        playsInline
                        loop
                      />
                    ) : (
                      <div className="pub-phone-placeholder">📹</div>
                    )}
                  </div>
                  <div className="pub-phone-overlay">
                    <div className="pub-phone-engagement">
                      <span>♡</span>
                      <span>💬</span>
                      <span>↗</span>
                    </div>
                    <div className="pub-phone-caption">
                      <strong>@ViralAI</strong>
                      <p>{caption || 'Your caption will appear here...'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Action Bar */}
        {!publishAccount && selectedPlatforms.has('youtube') && (
          <div className="pub-account-row">
            <span className="pub-account-label">Publishing account</span>
            <PublishAccountChip pa={pa} />
          </div>
        )}

        <div className="pub-action-bar">
          {scheduleEnabled ? (
            <>
              <button
                className="btn-primary pub-action-btn"
                disabled={publishing}
                aria-disabled={!!submitBlockReason}
                title={submitBlockReason || undefined}
                onClick={() => handlePublish(true, false)}
              >
                {publishing && publishMode === 'schedule' ? '⏳ Scheduling...' : `Schedule Post (${selectedPlatforms.size} Platform${selectedPlatforms.size !== 1 ? 's' : ''})`}
              </button>
              <button
                className="btn-secondary pub-action-btn"
                disabled={publishing}
                aria-disabled={!!submitBlockReason}
                title={submitBlockReason || undefined}
                onClick={() => handlePublish(false, false)}
              >
                {publishToLabel || 'Publish Now'}
              </button>
              <button
                className="btn-secondary pub-action-btn"
                disabled={publishing}
                aria-disabled={!!structuralBlockReason}
                title={structuralBlockReason || undefined}
                onClick={() => handlePublish(false, true)}
              >
                {publishing && publishMode === 'draft' ? '⏳ Saving Draft...' : 'Save as Draft'}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-primary pub-action-btn"
                disabled={publishing}
                aria-disabled={!!submitBlockReason}
                title={submitBlockReason || undefined}
                onClick={() => handlePublish(false, false)}
              >
                {publishing && publishMode === 'publish' ? '⏳ Publishing...' : (publishToLabel || `Publish Now (${selectedPlatforms.size} Platform${selectedPlatforms.size !== 1 ? 's' : ''})`)}
              </button>
              <button
                className="btn-secondary pub-action-btn"
                disabled={publishing}
                aria-disabled={!!structuralBlockReason}
                title={structuralBlockReason || undefined}
                onClick={() => handlePublish(false, true)}
              >
                {publishing && publishMode === 'draft' ? '⏳ Saving Draft...' : 'Save as Draft'}
              </button>
              <button
                className="btn-secondary pub-action-btn"
                disabled={publishing}
                aria-disabled={!!structuralBlockReason}
                title={structuralBlockReason || undefined}
                onClick={() => {
                  if (structuralBlockReason) { setPublishError(structuralBlockReason); return; }
                  setScheduleEnabled(true);
                }}
              >
                Schedule Post
              </button>
            </>
          )}
        </div>

        {/* Connect Account Modal */}
        <ConnectAccountModal
          open={connectModalOpen}
          onClose={() => {
            setConnectModalOpen(false);
            setSelectedPlatformToConnect(null);
          }}
          platform={selectedPlatformToConnect}
        />
    </>
  );

  if (embedded) {
    return <div className="pub-embed">{body}</div>;
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />
        {body}
      </main>
    </div>
  );
};

export default PublishDashboard;
